import Button from "@/common/components/Button";
import DropdownMenu from "@/common/components/DropdownMenu";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import Table from "@/common/components/Table";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import billFiscalDocumentsTableColumns from "../../constants/billFiscalDocumentsTableColumns.constant";
import billPaymentsTableColumns from "../../constants/billPaymentsTableColumns.constant";
import useBillingBill from "../../hooks/useBillingBill";
import { useBillingBillFiscalDocuments } from "../../hooks/useBillingBillFiscalDocuments";
import { useBillingBillPayments } from "../../hooks/useBillingBillPayments";
import { useBillingBillReissueFiscalDocument } from "../../hooks/useBillingBillReissueFiscalDocument";
import { useMarkBillingBillAsPaid } from "../../hooks/useMarkBillingBillAsPaid";
import { BillFiscalDocumentsTransformer } from "../../services/billFiscalDocumentsTransformer";
import { BillPaymentsTransformer } from "../../services/billPaymentsTransformer";
import AddFiscalDocumentModal from "./AddFiscalDocumentModal";
import AddPaymentModal from "./AddPaymentModal";
import ReportsModal from "./ReportsModal";
import SendToIntegrationProcessModal from "./SendToIntegrationProcessModal";
import { useQueryClient } from "@tanstack/react-query";
import ResendMailModal from "./ResendMailModal";
import { FormTransformers } from "@/common/utils/formTransformers";
import { MessageWithErrorExtras, ModalMessageWithErrorExtras } from "@/common/components/ModalMessageWithErrorExtras";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const DocumentsTab = () => {
  const { billId } = useParams();
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [errors, setErrors] = useState<MessageWithErrorExtras | boolean>(false)
  const { filterParams, setFilterParams } = useFilterParams();
  const queryClient = useQueryClient();

  const {
    data: bill,
    isPending: billIsPending
  } = useBillingBill({ id: billId });

  const { data: fiscalDocuments, meta: fiscalDocumentsMeta, isPending: fiscalDocumentsIsPending, error = {} as any } = useBillingBillFiscalDocuments({
    filters: { id: billId, page: Number(filterParams.page || 1) },
    mapper: Mapper.mapWithTransformer({ transformer: BillFiscalDocumentsTransformer.tableTransformer() }),
  });

  const { data: payments, meta: paymentsMeta, isPending: paymentsIsPending } = useBillingBillPayments({
    filters: { id: billId, page: Number(filterParams.paymentsPage || 1) },
    mapper: Mapper.mapWithTransformer({ transformer: BillPaymentsTransformer.tableTransformer() }),
  });

  const { mutate: reissueFiscalDocument, isPending: isReissueFiscalDocumentPending, isError: isReissueFiscalDocumentError } =
    useBillingBillReissueFiscalDocument({
      id: billId,
      onSuccess: () => setMessages(['Fatura enviada para processamento, aguarde alguns instantes ']),
      onError: FormTransformers.errorsWithExtrasTransformer({ setErrors }),
    });

  const { mutate: markBillingBillAsPaid, isPending: isMarkBillingBillAsPaidPending, isError: isMarkBillingBillAsPaidError } =
    useMarkBillingBillAsPaid({
      id: billId,
      onSuccess: (response) => {
        setMessages([response.data.message]);
        queryClient.invalidateQueries({ queryKey: ['billingBill', billId] });
      },
      onError: (error) => {
        if (error.response?.data.errors.base) setMessages(error.response?.data.errors.base)
      },
    });

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex justify-end gap-3">
            {billId && (
              <div className="flex items-center gap-3">
                <SendToIntegrationProcessModal billId={billId} bill={bill} />

                <ResendMailModal billId={billId} />
              </div>
            )}

            <DropdownMenu
              triggerLabel="Baixar fatura"
              itemClassName="min-w-[160px]"
              options={[
                {
                  label: 'PDF',
                  onClick: () => bill?.pdf?.url && window.open(bill?.pdf?.url, '_blank'),
                  disabled: !bill?.pdf?.url
                },
                {
                  label: 'CSV',
                  onClick: () => bill?.csv?.url && window.open(bill?.csv?.url, '_blank'),
                  disabled: !bill?.csv?.url
                },
                {
                  label: 'XLSX',
                  onClick: () => bill?.xlsx?.url && window.open(bill?.xlsx?.url, '_blank'),
                  disabled: !bill?.xlsx?.url
                },
                {
                  label: 'Relatórios',
                  onClick: () => setIsReportsModalOpen(true),
                  disabled: Number(bill?.reports?.length) === 0
                },
              ]}
            />

            <Button
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              disabled={!bill?.accepted_by_service_at || billIsPending || isReissueFiscalDocumentPending}
              isLoading={isReissueFiscalDocumentPending}
              onClick={() => reissueFiscalDocument()}
            >
              Solicitar reemissão de nota fiscal
            </Button>

            {(bill?.status === 'awaiting_payment_confirmation' || bill?.status === 'paid') && (
              <Button
                name="mark_billing_bill_as_paid"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                isLoading={isMarkBillingBillAsPaidPending || billIsPending}
                onClick={() => markBillingBillAsPaid()}
                operationKlassToCheckPermission="Admin::Billing::Bills::MarkAsPaid"
                disabled={billIsPending || isMarkBillingBillAsPaidPending || bill?.status === 'paid'}
              >
                {bill?.status === 'paid'
                  ? 'Fatura paga'
                  : 'Marca fatura como paga'}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h5>Notas Fiscais</h5>
            {billId && <AddFiscalDocumentModal billId={billId} />}
          </div>

          <ErrorMessageWrapper
            className="max-h-[300px]"
            statusCode={error?.status}
          >
            <Table
              className={twMerge('max-h-[200px]', fiscalDocuments?.length === 0 ? 'overflow-hidden [&>*]:-mt-20 pt-4' : '')}
              rows={fiscalDocuments}
              columns={billFiscalDocumentsTableColumns}
              isLoadingData={fiscalDocumentsIsPending}
              page={fiscalDocumentsMeta.current_page}
              totalPages={fiscalDocumentsMeta.total_pages}
              totalCount={fiscalDocumentsMeta.total}
              hasPagination
              setPage={(page) => setFilterParams((prev) => ({ ...prev, fiscalDocumentsPage: page }))}
              rowEmptyValue="--"
            />
          </ErrorMessageWrapper>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h5>Documentos de cobrança</h5>
            {billId && <AddPaymentModal billId={billId} />}
          </div>

          <Table
            className={twMerge('max-h-[200px]', payments?.length === 0 ? 'overflow-hidden [&>*]:-mt-20 pt-4' : '')}
            rows={payments}
            columns={billPaymentsTableColumns}
            isLoadingData={paymentsIsPending}
            page={paymentsMeta.current_page}
            totalPages={paymentsMeta.total_pages}
            totalCount={paymentsMeta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, paymentsPage: page }))}
            rowEmptyValue="--"
          />
        </div>
      </div>

      <ReportsModal isOpen={isReportsModalOpen} setIsOpen={setIsReportsModalOpen} bill={bill} />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isReissueFiscalDocumentError || isMarkBillingBillAsPaidError}
        />
      )}

      {errors && (
        <ModalMessageWithErrorExtras
          errors={errors}
          setErrors={setErrors}
        />
      )}
    </>
  );
};

export default DocumentsTab;
